import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { enablePush, pushSupported } from "@/lib/push";
import { initials, hexToHslString } from "@/lib/format";
import {
  LayoutDashboard, CheckSquare, ListTodo, FolderKanban, Calendar, Users, TrendingUp,
  Headphones, MessageSquare, Clock, FileCheck, StickyNote, BarChart3, Settings,
  Search, Plus, Bell, ChevronDown, LogOut, Menu, Moon, Sun, Sparkles, Building2, MoreHorizontal, WifiOff, ClipboardList, Image, ShoppingCart, ShieldCheck, FolderPlus, Loader2, Upload, Video, Plane,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ErrorBoundary from "@/components/ErrorBoundary";
import CommandPalette from "@/components/CommandPalette";
import NotificationBell from "@/components/NotificationBell";
import QuickCreate from "@/components/QuickCreate";
import AiAssistant from "@/components/AiAssistant";

const NAV = [
  { to: "/dashboard", label: "Genel Bakış", icon: LayoutDashboard },
  { to: "/my-work", label: "İşlerim", icon: CheckSquare },
  { to: "/tasks", label: "Görevler", icon: ListTodo },
  { to: "/projects", label: "Projeler", icon: FolderKanban },
  { to: "/calendar", label: "Takvim", icon: Calendar },
  { to: "/chat", label: "Sohbet", icon: MessageSquare },
  { to: "/time-tracking", label: "Zaman Takibi", icon: Clock },
  { to: "/requests", label: "Talepler ve Onaylar", icon: FileCheck },
  { to: "/leave", label: "İzinler", icon: Plane },
  { to: "/notes", label: "Notlar", icon: StickyNote },
  { to: "/meetings", label: "Toplantı Raporları", icon: ClipboardList },
  { to: "/video", label: "Görüntülü Toplantı", icon: Video },
  { to: "/designs", label: "Tasarım Onayları", icon: Image },
  { to: "/purchasing", label: "Satınalma Onayları", icon: ShoppingCart },
  { to: "/reports", label: "Raporlar", icon: BarChart3 },
  { to: "/settings", label: "Ayarlar", icon: Settings },
];

const MOBILE_PRIMARY = [
  { to: "/dashboard", label: "Genel", icon: LayoutDashboard },
  { to: "/my-work", label: "İşlerim", icon: CheckSquare },
  { to: "/tasks", label: "Görevler", icon: ListTodo },
  { to: "/calendar", label: "Takvim", icon: Calendar },
];

function OrgIcon({ org, className = "h-4 w-4" }) {
  if (org?.logo) return <img src={fileUrl(org.logo)} alt="" className={`${className} rounded object-cover`} />;
  return <Building2 className={className} />;
}

function SidebarNav({ onNavigate }) {
  const { canAccess, can } = useAuth();
  const isAdmin = can("manage_users");
  return (
    <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
      {isAdmin && (
        <NavLink to="/admin" onClick={onNavigate} data-testid="sidebar-nav-admin"
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-emerald-600 text-white" : "text-emerald-300 hover:bg-slate-800 hover:text-white"
            }`}>
          <ShieldCheck className="h-4 w-4 shrink-0" />
          <span className="truncate">Yönetim Paneli</span>
        </NavLink>
      )}
      {NAV.filter((n) => canAccess(n.to.slice(1))).map((n) => (
        <NavLink key={n.to} to={n.to} onClick={onNavigate}
          data-testid={`sidebar-nav-${n.to.slice(1)}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}>
          <n.icon className="h-4 w-4 shrink-0" />
          <span className="truncate">{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppLayout() {
  const { user, currentOrg, orgs, logout, switchOrg, canAccess, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("wh_theme") === "dark");
  const [offline, setOffline] = useState(!navigator.onLine);
  const [newOrgOpen, setNewOrgOpen] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: "", currency: "TRY", primary_color: "#4F46E5", logo: "" });
  const [orgSaving, setOrgSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const uploadOrgLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrgForm((f) => ({ ...f, logo: data.storage_path }));
    } catch { /* noop */ }
    finally { setLogoUploading(false); }
  };

  const createOrg = async () => {
    if (!orgForm.name.trim()) return;
    setOrgSaving(true);
    try {
      const { data } = await api.post("/orgs", {
        name: orgForm.name.trim(), currency: orgForm.currency,
        primary_color: orgForm.primary_color, logo: orgForm.logo,
      });
      await refresh();
      setNewOrgOpen(false);
      setOrgForm({ name: "", currency: "TRY", primary_color: "#4F46E5", logo: "" });
      if (data?.org?.id) switchOrg({ id: data.org.id });
    } catch { /* noop */ }
    finally { setOrgSaving(false); }
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("wh_theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const root = document.documentElement;
    const hsl = hexToHslString(currentOrg?.primary_color);
    if (hsl) {
      root.style.setProperty("--primary", hsl);
      root.style.setProperty("--ring", hsl);
    } else {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
    }
  }, [currentOrg?.primary_color]);

  useEffect(() => {
    if (pushSupported() && Notification.permission === "default" && !localStorage.getItem("wh_push_prompted")) {
      localStorage.setItem("wh_push_prompted", "1");
      const t = setTimeout(() => { enablePush().catch(() => {}); }, 2500);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    const on = () => setOffline(false), off = () => setOffline(true);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-slate-900 dark:bg-slate-950 dark:border-r dark:border-slate-800 shrink-0 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 px-4 h-16 border-b border-slate-800">
          <img src="/icon-192.png" alt="WorkHub" className="h-8 w-8 rounded-lg" />
          <span className="text-white font-bold font-display text-lg">WorkHub</span>
        </div>
        <SidebarNav />
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-2 text-slate-400 text-xs px-2">
            <OrgIcon org={currentOrg} className="h-3.5 w-3.5" /> {currentOrg?.name}
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="app-header sticky top-0 z-30 h-16 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b flex items-center gap-2 px-3 md:px-5">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 bg-slate-900 border-slate-800">
              <div className="flex items-center gap-2.5 px-4 h-16 border-b border-slate-800">
                <img src="/icon-192.png" alt="WorkHub" className="h-8 w-8 rounded-lg" />
                <span className="text-white font-bold font-display text-lg">WorkHub</span>
              </div>
              <SidebarNav onNavigate={() => {}} />
            </SheetContent>
          </Sheet>

          {/* Org switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2 max-w-[180px]" data-testid="org-switcher-dropdown">
                <OrgIcon org={currentOrg} className="h-4 w-4 shrink-0" />
                <span className="truncate text-sm">{currentOrg?.name || "Kuruluş"}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Kuruluşlar</DropdownMenuLabel>
              {orgs.map((o) => (
                <DropdownMenuItem key={o.id} onClick={() => o.id !== currentOrg?.id && switchOrg(o)} data-testid={`org-option-${o.id}`}>
                  <OrgIcon org={o} className="h-4 w-4 mr-2" /> {o.name}
                  {o.id === currentOrg?.id && <Badge variant="secondary" className="ml-auto text-[10px]">Aktif</Badge>}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setNewOrgOpen(true); }} data-testid="org-add-option" className="text-primary font-medium">
                <FolderPlus className="h-4 w-4 mr-2" /> Yeni Kuruluş Ekle
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button onClick={() => setCmdOpen(true)} data-testid="global-search-trigger"
            className="hidden md:flex items-center gap-2 text-sm text-muted-foreground bg-muted/60 hover:bg-muted rounded-lg px-3 h-9 flex-1 max-w-xs transition-colors">
            <Search className="h-4 w-4" /> Ara...
            <kbd className="ml-auto text-[10px] bg-background border rounded px-1.5 py-0.5">⌘K</kbd>
          </button>

          <div className="flex-1" />
          {offline && (
            <Badge variant="outline" className="gap-1 border-amber-300 text-amber-600" data-testid="offline-indicator">
              <WifiOff className="h-3 w-3" /> Çevrimdışı
            </Badge>
          )}
          <Button variant="ghost" size="icon" onClick={() => setCmdOpen(true)} className="md:hidden" data-testid="mobile-search-button"><Search className="h-5 w-5" /></Button>
          <QuickCreate />
          <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle">
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2" data-testid="profile-menu-trigger">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-indigo-600 text-white text-xs">{initials(user?.name)}</AvatarFallback></Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <Badge variant="secondary" className="mt-1.5 text-[10px]">{currentOrg?.role_label}</Badge>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/settings")}><Settings className="h-4 w-4 mr-2" /> Ayarlar</DropdownMenuItem>
              <DropdownMenuItem onClick={logout} className="text-rose-600" data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" /> Çıkış Yap</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 md:p-6 pb-24 lg:pb-6 max-w-[1600px] w-full mx-auto animate-in-up" key={loc.pathname}>
          <ErrorBoundary resetKey={loc.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="mobile-bottom-nav lg:hidden fixed bottom-0 inset-x-0 z-30 h-16 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t flex items-center justify-around px-2">
        {MOBILE_PRIMARY.filter((n) => canAccess(n.to.slice(1))).map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] px-2 py-1 rounded-md ${isActive ? "text-indigo-600" : "text-muted-foreground"}`}>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
        <Sheet>
          <SheetTrigger asChild>
            <button className="flex flex-col items-center gap-0.5 text-[10px] text-muted-foreground px-2 py-1" data-testid="mobile-nav-more">
              <MoreHorizontal className="h-5 w-5" /> Daha Fazla
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl">
            <div className="grid grid-cols-3 gap-3 py-4">
              {NAV.filter((n) => !MOBILE_PRIMARY.find((m) => m.to === n.to) && canAccess(n.to.slice(1))).map((n) => (
                <NavLink key={n.to} to={n.to} className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/50 text-xs text-center">
                  <n.icon className="h-5 w-5 text-indigo-600" /> {n.label}
                </NavLink>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </nav>

      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} />
      <AiAssistant />

      {/* New organization dialog */}
      <Dialog open={newOrgOpen} onOpenChange={setNewOrgOpen}>
        <DialogContent data-testid="new-org-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Kuruluş</DialogTitle>
            <DialogDescription>Yeni bir kuruluş oluşturun. Sahibi siz olursunuz ve buradan geçiş yapabilirsiniz.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Kuruluş Adı</Label>
              <Input value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") createOrg(); }} data-testid="new-org-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Para Birimi</Label>
                <Select value={orgForm.currency} onValueChange={(v) => setOrgForm({ ...orgForm, currency: v })}>
                  <SelectTrigger data-testid="new-org-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">₺ TRY</SelectItem>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                    <SelectItem value="GBP">£ GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Marka Rengi</Label>
                <div className="flex items-center gap-2 h-10">
                  <input type="color" value={orgForm.primary_color}
                    onChange={(e) => setOrgForm({ ...orgForm, primary_color: e.target.value })}
                    className="h-9 w-14 rounded cursor-pointer border" data-testid="new-org-color" />
                  <span className="text-xs text-muted-foreground">{orgForm.primary_color}</span>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Logo (opsiyonel)</Label>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg grid place-items-center overflow-hidden shrink-0" style={{ backgroundColor: orgForm.primary_color }}>
                  {orgForm.logo ? <img src={fileUrl(orgForm.logo)} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-5 w-5 text-white" />}
                </div>
                <label className="text-sm">
                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer hover:bg-muted transition-colors" data-testid="new-org-logo-btn">
                    {logoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {orgForm.logo ? "Değiştir" : "Logo Yükle"}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadOrgLogo(e.target.files?.[0])} data-testid="new-org-logo-input" />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOrgOpen(false)}>İptal</Button>
            <Button onClick={createOrg} disabled={orgSaving || !orgForm.name.trim()} data-testid="new-org-submit">
              {orgSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
